import type { CaixaTextoPlano } from '../types/caixaTextoPlano';

/**
 * Editor visual: xPct/wPct = % da largura da chapa; yPct/hPct = % do comprimento.
 * Impressão: posição trocada (igual às peças); tamanho w/h igual ao visual na folha.
 */

export function caixaTextoParaImpressaoSvg(
  c: CaixaTextoPlano,
  svgComprimento: number,
  svgLargura: number
) {
  return {
    x: (c.yPct / 100) * svgComprimento,
    y: (c.xPct / 100) * svgLargura,
    width: (c.wPct / 100) * svgLargura,
    height: (c.hPct / 100) * svgComprimento,
  };
}

/** Remove espaços/enters extras que inflam a caixa na impressão. */
export function normalizarTextoImpressaoCaixa(texto: string): string {
  return String(texto)
    .replace(/\r\n/g, '\n')
    .replace(/[ \t]+$/gm, '')
    .replace(/^\s+/, '')
    .replace(/\n+$/, '');
}

/** Estima quantas linhas o texto ocupa com quebra automática na largura dada (mm). */
export function estimarLinhasCaixaTexto(
  texto: string,
  larguraUtilMm: number,
  fontMm: number
): number {
  const largura = Math.max(fontMm * 2, larguraUtilMm);
  const charsPorLinha = Math.max(1, Math.floor(largura / (fontMm * 0.55)));
  const partes = texto.split('\n');
  let linhas = 0;
  for (const parte of partes) {
    const t = parte.trim();
    if (!t) {
      linhas += 1;
      continue;
    }
    linhas += Math.ceil(t.length / charsPorLinha);
  }
  return Math.max(1, linhas);
}

export interface CaixaTextoImpressaoLayout {
  x: number;
  y: number;
  width: number;
  height: number;
  padMm: number;
  fontMm: number;
  texto: string;
  nowrap: boolean;
}

/** Centro geométrico da caixa na impressão (eixos trocados como nas peças). */
export function centroCaixaImpressao(
  c: CaixaTextoPlano,
  svgComprimento: number,
  svgLargura: number
) {
  return {
    cx: ((c.yPct + c.hPct / 2) / 100) * svgComprimento,
    cy: ((c.xPct + c.wPct / 2) / 100) * svgLargura,
  };
}

/**
 * Impressão: caixa ajustada ao texto (com quebra na largura do desenho), centralizada no visual.
 */
export function layoutCaixaTextoImpressao(
  c: CaixaTextoPlano,
  svgComprimento: number,
  svgLargura: number
): CaixaTextoImpressaoLayout {
  const outer = caixaTextoParaImpressaoSvg(c, svgComprimento, svgLargura);
  const { cx, cy } = centroCaixaImpressao(c, svgComprimento, svgLargura);
  const texto = normalizarTextoImpressaoCaixa(c.texto);
  const padMm = 0.35;
  const borderMm = 0.8;

  if (!texto) {
    return {
      x: outer.x,
      y: outer.y,
      width: outer.width,
      height: outer.height,
      padMm,
      fontMm: 1,
      texto: '',
      nowrap: true,
    };
  }

  const fontMm = (c.fontSizePt * 25.4) / 72;
  const lineHeight = fontMm * 1.25;

  // Largura = caixa desenhada no projeto (quebra igual à tela)
  const tightW = Math.max(outer.width, fontMm * 4);
  const larguraUtil = tightW - padMm * 2 - borderMm;
  const numLines = estimarLinhasCaixaTexto(texto, larguraUtil, fontMm);
  const contentH = numLines * lineHeight;
  const tightH = contentH + padMm * 2 + borderMm;

  const nowrap =
    numLines === 1 && !texto.includes('\n') && texto.length * fontMm * 0.55 <= larguraUtil;

  return {
    x: cx - tightW / 2,
    y: cy - tightH / 2,
    width: tightW,
    height: tightH,
    padMm,
    fontMm,
    texto,
    nowrap,
  };
}

export type ResizeHandle = 'n' | 's' | 'e' | 'w' | 'ne' | 'nw' | 'se' | 'sw';

export interface CaixaPx {
  x: number;
  y: number;
  w: number;
  h: number;
}

export function caixaParaPx(c: CaixaTextoPlano, chapaW: number, chapaH: number): CaixaPx {
  return {
    x: (c.xPct / 100) * chapaW,
    y: (c.yPct / 100) * chapaH,
    w: (c.wPct / 100) * chapaW,
    h: (c.hPct / 100) * chapaH,
  };
}

export function pxParaCaixa(px: CaixaPx, chapaW: number, chapaH: number): Pick<CaixaTextoPlano, 'xPct' | 'yPct' | 'wPct' | 'hPct'> {
  return {
    xPct: (px.x / chapaW) * 100,
    yPct: (px.y / chapaH) * 100,
    wPct: (px.w / chapaW) * 100,
    hPct: (px.h / chapaH) * 100,
  };
}

/** Movimento do mouse na tela → incremento nos eixos largura/comprimento da chapa. */
export function telaParaChapaWH(dxTela: number, dyTela: number, rotacaoDesenho: number) {
  const rot = ((rotacaoDesenho % 360) + 360) % 360;
  switch (rot) {
    case 90:
      return { dW: dyTela, dH: dxTela };
    case 180:
      return { dW: -dxTela, dH: -dyTela };
    case 270:
      return { dW: -dyTela, dH: dxTela };
    default:
      return { dW: dxTela, dH: dyTela };
  }
}

/** true = eixo W da chapa (largura) acompanha horizontal da tela */
function chapaWAcompanhaHorizontal(rotacaoDesenho: number) {
  const rot = ((rotacaoDesenho % 360) + 360) % 360;
  return rot === 0 || rot === 180;
}

/**
 * Redimensiona estilo Excel (contrarrotacionada):
 * laterais esq/dir → altura · topo/baixo → largura · cantos → ambos.
 * Handles centrais: canto superior-esquerdo fixo (só muda w/h, sem mover x/y).
 */
export function redimensionarCaixaTela(
  orig: CaixaPx,
  handle: ResizeHandle,
  dxTela: number,
  dyTela: number,
  rotacaoDesenho: number,
  minW: number,
  minH: number
): CaixaPx {
  let { x, y, w, h } = orig;
  const wHoriz = chapaWAcompanhaHorizontal(rotacaoDesenho);

  const horiz = telaParaChapaWH(dxTela, 0, rotacaoDesenho);
  const vert = telaParaChapaWH(0, dyTela, rotacaoDesenho);
  const both = telaParaChapaWH(dxTela, dyTela, rotacaoDesenho);

  const deltaHoriz = () => (wHoriz ? horiz.dW : horiz.dH);
  const deltaVert = () => (wHoriz ? vert.dH : vert.dW);
  const deltaBothH = () => (wHoriz ? both.dW : both.dH);
  const deltaBothV = () => (wHoriz ? both.dH : both.dW);

  const growHoriz = (d: number) => {
    if (wHoriz) w += d;
    else h += d;
  };
  const shrinkHoriz = (d: number) => {
    if (wHoriz) {
      x += d;
      w -= d;
    } else {
      y += d;
      h -= d;
    }
  };
  /** Topo/baixo encolhendo: largura muda sem mover posição (canto sup-esq fixo). */
  const shrinkHorizFixo = (d: number) => {
    if (wHoriz) w -= d;
    else h -= d;
  };
  const growVert = (d: number) => {
    if (wHoriz) h += d;
    else w += d;
  };
  const shrinkVert = (d: number) => {
    if (wHoriz) {
      y += d;
      h -= d;
    } else {
      x += d;
      w -= d;
    }
  };
  /** Esq/dir encolhendo: altura muda sem mover posição (canto sup-esq fixo). */
  const shrinkVertFixo = (d: number) => {
    if (wHoriz) h -= d;
    else w -= d;
  };

  switch (handle) {
    case 'e':
      growVert(deltaHoriz());
      break;
    case 'w':
      shrinkVertFixo(deltaHoriz());
      break;
    case 's':
      growHoriz(deltaVert());
      break;
    case 'n':
      shrinkHorizFixo(deltaVert());
      break;
    case 'se':
      growVert(deltaBothH());
      growHoriz(deltaBothV());
      break;
    case 'sw':
      shrinkVert(deltaBothH());
      growHoriz(deltaBothV());
      break;
    case 'ne':
      growVert(deltaBothH());
      shrinkHoriz(deltaBothV());
      break;
    case 'nw':
      shrinkVert(deltaBothH());
      shrinkHoriz(deltaBothV());
      break;
  }

  if (w < minW) {
    if (handle === 'nw' || handle === 'sw') x -= minW - w;
    w = minW;
  }
  if (h < minH) {
    if (handle === 'nw' || handle === 'ne') y -= minH - h;
    h = minH;
  }

  return { x, y, w, h };
}

export const RESIZE_HANDLES: Array<{
  id: ResizeHandle;
  className: string;
  cursor: string;
}> = [
  { id: 'nw', className: '-top-1 -left-1', cursor: 'nwse-resize' },
  { id: 'n', className: '-top-1 left-1/2 -translate-x-1/2', cursor: 'ns-resize' },
  { id: 'ne', className: '-top-1 -right-1', cursor: 'nesw-resize' },
  { id: 'e', className: 'top-1/2 -right-1 -translate-y-1/2', cursor: 'ew-resize' },
  { id: 'se', className: '-bottom-1 -right-1', cursor: 'nwse-resize' },
  { id: 's', className: '-bottom-1 left-1/2 -translate-x-1/2', cursor: 'ns-resize' },
  { id: 'sw', className: '-bottom-1 -left-1', cursor: 'nesw-resize' },
  { id: 'w', className: 'top-1/2 -left-1 -translate-y-1/2', cursor: 'ew-resize' },
];
